import { useCallback, useState } from 'react';
import { ArrowLeft, ArrowRight, Package } from 'shared/icons';
import api from '../../../services/api';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { Mono, Sku } from '../../ui/Mono';
import {
    BigAction,
    CodeCard,
    DangerScreenAction,
    FailureScreen,
    PlainAddress,
    ScanFlash,
    StepShell,
    SuccessScreen,
    WaitScreen,
} from '../collector/CollectorChrome';
import ScanInput from '../collector/ScanInput';
import { PlayMovementDone, PlayScanError, PlayScanSuccess } from '../collector/collectorFeedback';
import { CodesMatch, ParseScanResult, SCAN_METHOD_SCANNED, SCAN_METHOD_TYPED, SCAN_TYPE_LPN } from '../collector/collectorScan';
import BenchNumberPad, { ParsePadValue } from '../production/BenchNumberPad';
import BalancePreview from './BalancePreview';
import FifoNotice from './FifoNotice';
import { MergeLpns, PreviewRepack, SplitLpn } from './repackApi';
import {
    FormatQty,
    IsLpnCreationForbiddenError,
    ParseRepackBalance,
    ParseRepackWarnings,
    REPACK_MERGE,
    REPACK_SPLIT,
} from './repackModel';

/**
 * REPACK NO CHÃO (M7) — split/merge guiado por bipes, na gramática do M4/M5.
 *
 * ── A cadeia ────────────────────────────────────────────────────────────────
 *   1. bipa o pallet de ORIGEM  →  2. escolhe O QUE sai  →  3. bipa o pallet de
 *   DESTINO  →  4. informa a QUANTIDADE  →  conferência (saldo do servidor) →
 *   grava.
 * Uma tela por passo, endereço/código em tamanho de leitura à distância. No
 * quiosque (/coletor) é SÓ bipe — sem digitação, por ordem do dono de 26/09;
 * no console a digitação continua e grava `TYPED` (`ScanInput` cuida das duas
 * regras; a trilha recebe o PIOR caso da cadeia, como no movimento avulso do
 * M4).
 *
 * ── A regra que o chão precisa saber ────────────────────────────────────────
 * **No coletor o pallet de destino já tem que existir** (etiquetado). Criar LPN
 * é ato do Supervisor (§6.2) e o backend responde 403 quando alguém tenta —
 * então essa resposta NÃO vira tela vermelha: vira INSTRUÇÃO ("peça a etiqueta
 * ao Supervisor"), em âmbar, que é a cor de "nada está errado e mesmo assim não
 * dá para seguir". Vermelho aqui ensinaria o chão a ignorar o vermelho.
 *
 * ── Por que o passo 0 pergunta split × merge ────────────────────────────────
 * O gesto é o mesmo, mas a TRILHA não: `SPLIT` e `MERGE` são kinds diferentes
 * em `wms_stock_movements`, e só quem está com o pallet na frente sabe se está
 * tirando um pedaço ou esvaziando o pallet no outro. Derivar isso da quantidade
 * seria inventar regra que o PRD não tem.
 */

const PHASE = {
    CHOOSE: 'CHOOSE',
    SCAN_SOURCE: 'SCAN_SOURCE',
    PICK_ITEM: 'PICK_ITEM',
    SCAN_TARGET: 'SCAN_TARGET',
    QTY: 'QTY',
    CONFIRM: 'CONFIRM',
    MISMATCH: 'MISMATCH',
    FORBIDDEN: 'FORBIDDEN',
    FAILED: 'FAILED',
    DONE: 'DONE',
};

const STEP_NAMES = ['SOURCE', 'ITEM', 'TARGET', 'QTY'];

const INITIAL_STATE = {
    phase: PHASE.CHOOSE,
    operation: REPACK_SPLIT,
    source: null,
    sourceItems: [],
    item: null,
    target: null,
    qtyText: '',
    typedInChain: false,
    mismatch: null,
    failure: null,
    preview: null,
    result: null,
};

const RepackCollectorFlow = ({ label, operator, onExit }) => {
    const [state, setState] = useState(INITIAL_STATE);
    const [busy, setBusy] = useState(false);
    const [flash, setFlash] = useState(null);

    const patch = useCallback((changes) => setState((current) => ({ ...current, ...changes })), []);

    const succeed = useCallback((message, changes) => {
        PlayScanSuccess();
        setFlash(message);
        setState((current) => ({ ...current, ...changes }));
    }, []);

    const fail = useCallback((mismatch) => {
        PlayScanError();
        setState((current) => ({ ...current, phase: PHASE.MISMATCH, mismatch }));
    }, []);

    const rememberMethod = useCallback((scanMethod) => {
        if (scanMethod === SCAN_METHOD_TYPED) {
            setState((current) => ({ ...current, typedInChain: true }));
        }
    }, []);

    const restart = useCallback(() => setState({ ...INITIAL_STATE, phase: PHASE.CHOOSE }), []);

    /** Classifica o bipe no backend — §2.2: o parsing é de lá, um lugar só. */
    const classifyLpn = useCallback(async (code) => {
        const payload = await api.scanWarehouseCode(code);
        return ParseScanResult(payload);
    }, []);

    // ── Passo 1 — ORIGEM ────────────────────────────────────────────────────
    const handleSourceScan = useCallback(
        async (code, scanMethod) => {
            setBusy(true);
            rememberMethod(scanMethod);
            try {
                const parsed = await classifyLpn(code);
                if (parsed.type !== SCAN_TYPE_LPN || !parsed.lpn) {
                    fail({
                        title: label('wms.collector.mismatch_type_title'),
                        message: label('wms.collector.mismatch_expected_lpn'),
                        read: code,
                        retryPhase: PHASE.SCAN_SOURCE,
                    });
                    return;
                }
                const items = parsed.items || [];
                succeed(label('wms.repack.collector.flash_source'), {
                    phase: items.length === 1 ? PHASE.SCAN_TARGET : PHASE.PICK_ITEM,
                    source: parsed.lpn,
                    sourceItems: items,
                    item: items.length === 1 ? items[0] : null,
                });
            } catch (error) {
                fail({
                    title: label('wms.collector.scan_failed_title'),
                    message: error?.message || label('wms.common.load_error'),
                    read: code,
                    retryPhase: PHASE.SCAN_SOURCE,
                });
            } finally {
                setBusy(false);
            }
        },
        [classifyLpn, fail, label, rememberMethod, succeed],
    );

    // ── Passo 3 — DESTINO (tem que EXISTIR: §6.2) ───────────────────────────
    const handleTargetScan = useCallback(
        async (code, scanMethod) => {
            rememberMethod(scanMethod);
            if (state.source?.code && CodesMatch(state.source.code, code)) {
                fail({
                    title: label('wms.repack.collector.same_lpn_title'),
                    message: label('wms.repack.collector.same_lpn_hint'),
                    expected: label('wms.collector.expected_any_lpn'),
                    read: code,
                    retryPhase: PHASE.SCAN_TARGET,
                });
                return;
            }
            setBusy(true);
            try {
                const parsed = await classifyLpn(code);
                if (parsed.type !== SCAN_TYPE_LPN || !parsed.lpn) {
                    fail({
                        title: label('wms.collector.mismatch_type_title'),
                        // O destino do chão já tem que estar etiquetado — é a
                        // primeira coisa que o operador precisa ler aqui.
                        message: label('wms.repack.collector.step_target_hint'),
                        read: code,
                        retryPhase: PHASE.SCAN_TARGET,
                    });
                    return;
                }
                succeed(label('wms.repack.collector.flash_target'), {
                    phase: PHASE.QTY,
                    target: parsed.lpn,
                });
            } catch (error) {
                fail({
                    title: label('wms.collector.scan_failed_title'),
                    message: error?.message || label('wms.common.load_error'),
                    read: code,
                    retryPhase: PHASE.SCAN_TARGET,
                });
            } finally {
                setBusy(false);
            }
        },
        [classifyLpn, fail, label, rememberMethod, state.source, succeed],
    );

    // ── O corpo do wire: o MESMO da prévia e da gravação ────────────────────
    const buildBody = useCallback(() => {
        const qty = ParsePadValue(state.qtyText);
        const line = { product_id: state.item?.product_id, qty };
        if (state.item?.supplier_lot) {
            line.supplier_lot = state.item.supplier_lot;
        }
        const common = {
            scan_method: state.typedInChain ? SCAN_METHOD_TYPED : SCAN_METHOD_SCANNED,
            // QUEM FEZ: o crachá da sessão. A conta do coletor é COMPARTILHADA
            // — sem isto a autoria da trilha seria a do dispositivo.
            operator_code: operator?.employee_code || undefined,
        };
        if (state.operation === REPACK_SPLIT) {
            return {
                ...common,
                source_lpn_code: state.source?.code,
                destinations: [{ lpn_code: state.target?.code, items: [line] }],
            };
        }
        return {
            ...common,
            sources: [{ lpn_code: state.source?.code, items: [line] }],
            target_lpn_code: state.target?.code,
        };
    }, [operator, state.item, state.operation, state.qtyText, state.source, state.target, state.typedInChain]);

    /**
     * A CONFERÊNCIA: pede a prévia ao servidor (`dry_run`) e mostra o saldo
     * antes × depois com a conta DELE. É a mesma disciplina do console — o chão
     * também merece ver o que vai acontecer antes de acontecer.
     */
    const goToConfirm = useCallback(async () => {
        setBusy(true);
        try {
            const payload = await PreviewRepack({ operation: state.operation, ...buildBody() });
            patch({
                phase: PHASE.CONFIRM,
                preview: { balance: ParseRepackBalance(payload), warnings: ParseRepackWarnings(payload) },
            });
        } catch (error) {
            PlayScanError();
            if (IsLpnCreationForbiddenError(error)) {
                patch({ phase: PHASE.FORBIDDEN });
                return;
            }
            patch({ phase: PHASE.FAILED, failure: error });
        } finally {
            setBusy(false);
        }
    }, [buildBody, patch, state.operation]);

    const commit = useCallback(async () => {
        setBusy(true);
        try {
            const body = buildBody();
            const payload = state.operation === REPACK_SPLIT ? await SplitLpn(body) : await MergeLpns(body);
            PlayMovementDone();
            patch({ phase: PHASE.DONE, result: payload });
        } catch (error) {
            PlayScanError();
            if (IsLpnCreationForbiddenError(error)) {
                patch({ phase: PHASE.FORBIDDEN });
                return;
            }
            patch({ phase: PHASE.FAILED, failure: error });
        } finally {
            setBusy(false);
        }
    }, [buildBody, patch, state.operation]);

    const flashNode = <ScanFlash message={flash} onDone={() => setFlash(null)} />;

    // ── Telas de exceção ────────────────────────────────────────────────────
    if (state.phase === PHASE.MISMATCH) {
        return (
            <>
                {flashNode}
                <FailureScreen
                    label={label}
                    title={state.mismatch?.title}
                    message={state.mismatch?.message}
                    expected={state.mismatch?.expected}
                    read={state.mismatch?.read}
                    actions={
                        <>
                            <BigAction
                                variant="secondary"
                                onClick={() =>
                                    patch({ phase: state.mismatch?.retryPhase || PHASE.SCAN_SOURCE, mismatch: null })
                                }
                            >
                                {label('wms.collector.try_again')}
                            </BigAction>
                            <DangerScreenAction onClick={restart}>
                                {label('wms.collector.restart_flow')}
                            </DangerScreenAction>
                        </>
                    }
                />
            </>
        );
    }

    // 403 = REGRA, não erro: o pallet de destino tem que vir etiquetado do
    // Supervisor. Âmbar (nada quebrou) e com a instrução do que fazer.
    if (state.phase === PHASE.FORBIDDEN) {
        return (
            <WaitScreen
                title={label('wms.repack.collector.forbidden_title')}
                message={label('wms.repack.collector.forbidden_body')}
                actions={
                    <>
                        <BigAction onClick={() => patch({ phase: PHASE.SCAN_TARGET, target: null })}>
                            {label('wms.collector.try_again')}
                        </BigAction>
                        <Button variant="ghost" size="lg" className="w-full" onClick={onExit}>
                            {label('wms.collector.back_to_menu')}
                        </Button>
                    </>
                }
            />
        );
    }

    if (state.phase === PHASE.FAILED) {
        return (
            <FailureScreen
                label={label}
                title={label('wms.repack.collector.refused_title')}
                message={state.failure?.message || label('wms.common.load_error')}
                actions={
                    <>
                        <BigAction variant="secondary" onClick={() => patch({ phase: PHASE.QTY, failure: null })}>
                            {label('wms.collector.try_again')}
                        </BigAction>
                        <DangerScreenAction onClick={restart}>
                            {label('wms.collector.restart_flow')}
                        </DangerScreenAction>
                    </>
                }
            />
        );
    }

    if (state.phase === PHASE.DONE) {
        const balance = ParseRepackBalance(state.result);
        return (
            <>
                {flashNode}
                <SuccessScreen
                    title={label('wms.repack.collector.done')}
                    actions={
                        <>
                            <BigAction onClick={restart}>{label('wms.repack.collector.new_repack')}</BigAction>
                            <Button variant="ghost" size="lg" className="w-full" onClick={onExit}>
                                {label('wms.collector.back_to_menu')}
                            </Button>
                        </>
                    }
                >
                    <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1 rounded-xl bg-white/70 px-3 py-3 text-center dark:bg-mono-950/60">
                            <Mono className="block break-all text-lg font-black text-black dark:text-white">
                                {state.source?.code}
                            </Mono>
                        </div>
                        <ArrowRight size={26} className="shrink-0 text-success-700 dark:text-success-400" />
                        <div className="min-w-0 flex-1 rounded-xl bg-white/70 px-3 py-3 text-center dark:bg-mono-950/60">
                            <Mono className="block break-all text-lg font-black text-black dark:text-white">
                                {state.target?.code}
                            </Mono>
                        </div>
                    </div>
                    <p className="text-center text-base font-semibold text-success-text dark:text-success-200">
                        {`${FormatQty(ParsePadValue(state.qtyText) ?? 0)} · ${state.item?.sku ?? ''}`}
                    </p>
                    <p className="text-center text-sm text-mono-600 dark:text-mono-400">
                        {`${label('wms.collector.scan_method')}: `}
                        <Badge variant={state.typedInChain ? 'warning' : 'success'}>
                            {label(
                                `wms.collector.scan_method.${state.typedInChain ? SCAN_METHOD_TYPED : SCAN_METHOD_SCANNED}`,
                            )}
                        </Badge>
                    </p>
                    {balance ? <BalancePreview balance={balance} label={label} compact /> : null}
                </SuccessScreen>
            </>
        );
    }

    // ── Passo 0 — split ou merge ────────────────────────────────────────────
    if (state.phase === PHASE.CHOOSE) {
        return (
            <StepShell
                title={label('wms.repack.collector.choose_title')}
                footer={
                    <Button variant="ghost" size="lg" className="w-full" onClick={onExit}>
                        <ArrowLeft size={20} className="mr-2" />
                        {label('wms.collector.back_to_menu')}
                    </Button>
                }
            >
                <div className="space-y-3">
                    {[
                        {
                            operation: REPACK_SPLIT,
                            title: label('wms.repack.collector.split'),
                            hint: label('wms.repack.collector.split_hint'),
                        },
                        {
                            operation: REPACK_MERGE,
                            title: label('wms.repack.collector.merge'),
                            hint: label('wms.repack.collector.merge_hint'),
                        },
                    ].map((choice) => (
                        <button
                            key={choice.operation}
                            type="button"
                            onClick={() => patch({ operation: choice.operation, phase: PHASE.SCAN_SOURCE })}
                            className="w-full rounded-2xl border-2 border-mono-300 bg-white px-4 py-5 text-center transition-colors hover:bg-mono-50 dark:border-mono-700 dark:bg-mono-950 dark:hover:bg-mono-900"
                        >
                            <span className="block text-2xl font-black text-black dark:text-white">{choice.title}</span>
                            <span className="mt-1 block text-sm text-mono-500 dark:text-mono-400">{choice.hint}</span>
                        </button>
                    ))}
                </div>
            </StepShell>
        );
    }

    // ── Passos com bipe/escolha ─────────────────────────────────────────────
    const stepIndex =
        state.phase === PHASE.SCAN_SOURCE
            ? 0
            : state.phase === PHASE.PICK_ITEM
              ? 1
              : state.phase === PHASE.SCAN_TARGET
                ? 2
                : 3;
    const stepName = `${label('wms.collector.step')} ${stepIndex + 1}/${STEP_NAMES.length} · ${label(
        `wms.repack.collector.step_name.${STEP_NAMES[stepIndex]}`,
    )}`;

    const sourceBanner = state.source ? (
        <div className="rounded-xl bg-mono-50 px-3 py-2 text-center dark:bg-mono-900">
            <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                {label('wms.collector.from')}
            </p>
            <Mono className="block break-all text-base font-bold text-black dark:text-white">
                {state.source.code}
            </Mono>
        </div>
    ) : null;

    const qty = ParsePadValue(state.qtyText);
    const available = Number(state.item?.qty ?? 0);

    const footer =
        state.phase === PHASE.QTY ? (
            <>
                <BigAction onClick={goToConfirm} loading={busy} disabled={qty === null || !(qty > 0)}>
                    {label('wms.repack.collector.confirm')}
                </BigAction>
                <Button
                    variant="secondary"
                    size="lg"
                    className="w-full"
                    disabled={busy}
                    onClick={() => patch({ qtyText: String(available).replace('.', ',') })}
                >
                    {label('wms.repack.collector.move_all')}
                </Button>
                <Button variant="ghost" size="lg" className="w-full" onClick={restart} disabled={busy}>
                    {label('wms.collector.restart_flow')}
                </Button>
            </>
        ) : state.phase === PHASE.CONFIRM ? (
            <>
                <BigAction onClick={commit} loading={busy} disabled={state.preview?.balance?.balanced === false}>
                    {label('wms.repack.collector.confirm')}
                </BigAction>
                <Button variant="ghost" size="lg" className="w-full" onClick={() => patch({ phase: PHASE.QTY })}>
                    {label('wms.collector.try_again')}
                </Button>
            </>
        ) : (
            <Button
                variant="ghost"
                size="lg"
                className="w-full"
                onClick={stepIndex === 0 ? () => patch({ phase: PHASE.CHOOSE }) : restart}
            >
                <ArrowLeft size={20} className="mr-2" />
                {stepIndex === 0 ? label('wms.collector.back_to_menu') : label('wms.collector.restart_flow')}
            </Button>
        );

    return (
        <>
            {flashNode}
            <StepShell
                stepIndex={stepIndex}
                stepCount={STEP_NAMES.length}
                stepName={stepName}
                // Nos passos de bipe quem faz o papel de título é o `PlainAddress`
                // (o verbo grande). O passo da quantidade não tem endereço para
                // mostrar, então o título vem aqui.
                title={state.phase === PHASE.QTY ? label('wms.repack.collector.step_qty') : null}
                footer={footer}
            >
                {state.phase === PHASE.SCAN_SOURCE ? (
                    <>
                        <PlainAddress>{label('wms.repack.collector.step_source')}</PlainAddress>
                        <p className="text-center text-base text-mono-600 dark:text-mono-400">
                            {label('wms.repack.collector.step_source_hint')}
                        </p>
                        <ScanInput
                            label={label}
                            onScan={handleSourceScan}
                            disabled={busy}
                            focusKey={state.phase}
                            manualPlaceholder="MP-260811-001"
                        />
                    </>
                ) : null}

                {state.phase === PHASE.PICK_ITEM ? (
                    <>
                        {sourceBanner}
                        <PlainAddress>{label('wms.repack.collector.step_item')}</PlainAddress>
                        <div className="space-y-2">
                            {state.sourceItems.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => patch({ item, phase: PHASE.SCAN_TARGET })}
                                    className="flex w-full items-center gap-3 rounded-2xl border-2 border-mono-300 bg-white px-4 py-4 text-left transition-colors hover:bg-mono-50 dark:border-mono-700 dark:bg-mono-950 dark:hover:bg-mono-900"
                                >
                                    <Package size={26} className="shrink-0 text-mono-500" />
                                    <span className="min-w-0 flex-1">
                                        <Sku value={item.sku} className="block text-lg font-bold" />
                                        <span className="block truncate text-xs text-mono-500 dark:text-mono-400">
                                            {item.supplier_lot || label('wms.repack.without_lot')}
                                        </span>
                                    </span>
                                    <Mono className="shrink-0 text-lg font-black text-black dark:text-white">
                                        {FormatQty(item.qty)}
                                    </Mono>
                                </button>
                            ))}
                        </div>
                    </>
                ) : null}

                {state.phase === PHASE.SCAN_TARGET ? (
                    <>
                        {sourceBanner}
                        <PlainAddress>{label('wms.repack.collector.step_target')}</PlainAddress>
                        <p className="text-center text-base text-mono-600 dark:text-mono-400">
                            {label('wms.repack.collector.step_target_hint')}
                        </p>
                        <ScanInput
                            label={label}
                            onScan={handleTargetScan}
                            disabled={busy}
                            focusKey={state.phase}
                            manualPlaceholder="REV-260811-002"
                        />
                    </>
                ) : null}

                {state.phase === PHASE.QTY ? (
                    <>
                        <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1 rounded-xl bg-mono-50 px-2 py-2 text-center dark:bg-mono-900">
                                <Mono className="block break-all text-sm font-bold text-black dark:text-white">
                                    {state.source?.code}
                                </Mono>
                            </div>
                            <ArrowRight size={20} className="shrink-0 text-mono-400" />
                            <div className="min-w-0 flex-1 rounded-xl bg-mono-50 px-2 py-2 text-center dark:bg-mono-900">
                                <Mono className="block break-all text-sm font-bold text-black dark:text-white">
                                    {state.target?.code}
                                </Mono>
                            </div>
                        </div>
                        <CodeCard caption={label('wms.lpn.field.supplier_lot')} tone="success">
                            {state.item?.supplier_lot || state.item?.sku || '—'}
                        </CodeCard>
                        <BenchNumberPad
                            label={label}
                            value={state.qtyText}
                            onChange={(value) => patch({ qtyText: value })}
                            hint={`${label('wms.repack.available')}: ${FormatQty(available)}`}
                            disabled={busy}
                        />
                    </>
                ) : null}

                {state.phase === PHASE.CONFIRM ? (
                    <>
                        <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                            {label('wms.repack.collector.balance_line')}
                        </h3>
                        {/* FIFO avisa e NÃO trava: o botão de confirmar do rodapé
                            continua habilitado. */}
                        <FifoNotice warnings={state.preview?.warnings || []} label={label} />
                        <BalancePreview balance={state.preview?.balance} label={label} compact />
                    </>
                ) : null}
            </StepShell>
        </>
    );
};

export default RepackCollectorFlow;
